import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { Documents } from '@/types/company';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { Form, Formik } from 'formik';
import { toString } from 'lodash';
import { useTranslation } from 'react-i18next';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import * as Yup from 'yup';
import { OwnSharesField, OwnSharesType } from './OwnSharesField';

interface AssetsStepProps {
  formData: Partial<Documents.DocumentType['data']>;
  currency: string;
  onMovingForward: (values: Partial<Documents.DocumentType['data']>) => void;
  onMovingBackward: () => void;
  next?: boolean;
}

export const AssetsStep = ({
  formData,
  currency,
  onMovingForward,
  onMovingBackward,
  next,
}: AssetsStepProps) => {
  const { t } = useTranslation();

  const assetsCurrency = formData?.Assets_Currency || currency;

  const initialValues = {
    primary_home: formData?.Primary_Home || '',
    home_ownership: formData?.Home_Ownership || 0,
    investment_properties: formData?.Investment_Properties || '',
    cash: formData?.Cash || '',
    own_shares: getOwnSharesValue(formData?.Own_Shares) || '',
    own_shares_type: isOwnSharesPercentage(formData?.Own_Shares)
      ? OwnSharesType.Percentage
      : OwnSharesType.Currency,
    listed_shares: formData?.Listed_Shares || '',
    pension: formData?.Pension || '',
    vehicles: formData?.Vehicles || '',
  };

  const validationSchema = Yup.object({
    primary_home: Yup.string().required('Required'),
    home_ownership: Yup.number()
      .typeError('Must be a number')
      .min(0)
      .max(100)
      .required('Required'),
    investment_properties: Yup.string().required('Required'),
    cash: Yup.string().required('Required'),
    own_shares: Yup.string()
      .required('Required')
      .test('ownSharesValidation', 'Invalid value', function (value) {
        const { own_shares_type } = this.parent;
        if (!value) return false;

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return false;

        if (own_shares_type === OwnSharesType.Percentage) {
          return numValue >= 1 && numValue <= 100;
        }
        return numValue >= 0;
      }),
    own_shares_type: Yup.string()
      .oneOf([OwnSharesType.Currency, OwnSharesType.Percentage])
      .required('Required'),
    listed_shares: Yup.string().required('Required'),
    pension: Yup.string().required('Required'),
    vehicles: Yup.string().required('Required'),
  });

  const handleSubmit = (values: typeof initialValues) => {
    onMovingForward({
      Assets_Currency: assetsCurrency,
      Primary_Home: values.primary_home,
      Home_Ownership: values.home_ownership,
      Investment_Properties: values.investment_properties,
      Cash: values.cash,
      Own_Shares:
        values.own_shares_type === OwnSharesType.Percentage
          ? `${values.own_shares}%`
          : values.own_shares,
      Listed_Shares: values.listed_shares,
      Pension: values.pension,
      Vehicles: values.vehicles,
    });
  };

  return (
    <WizardAnimationWrapper next={next}>
      <StepLayout
        onMovingBackward={onMovingBackward}
        containerProps={{
          gap: 1,
        }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {(formik) => {
            return (
              <Form>
                <Stack width="100%" gap={3}>
                  <Box>
                    <Typography
                      variant="h4"
                      color="text.primary"
                      marginBottom={1}>
                      {t('assets')}
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {t('assets_subtitle')}
                    </Typography>
                  </Box>
                  <Row rowSpacing={3}>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('primary_home')}
                        name="primary_home"
                        type="text"
                        value={formik.values.primary_home}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.primary_home &&
                          !!formik.errors.primary_home
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={
                          formik.touched.primary_home &&
                          formik.errors.primary_home
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('home_ownership')}
                        name="home_ownership"
                        type="text"
                        value={formik.values.home_ownership}
                        onValueChange={(values: NumberFormatValues) => {
                          formik.setFieldValue(
                            'home_ownership',
                            values.floatValue,
                            true
                          );
                        }}
                        error={
                          formik.touched.home_ownership &&
                          !!formik.errors.home_ownership
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                %
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                            max: 100,
                          },
                        }}
                        helperText={
                          formik.touched.home_ownership &&
                          formik.errors.home_ownership
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                  </Row>

                  <Row rowSpacing={3}>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('investment_properties')}
                        name="investment_properties"
                        type="text"
                        value={formik.values.investment_properties}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.investment_properties &&
                          !!formik.errors.investment_properties
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={
                          formik.touched.investment_properties &&
                          formik.errors.investment_properties
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Row>
                        <Col xs={4}>
                          <FormControl fullWidth>
                            <Select
                              id="own_shares_type"
                              value={formik.values.own_shares_type}
                              onChange={(event) => {
                                formik.setFieldValue(
                                  'own_shares_type',
                                  event.target.value
                                );
                                formik.setFieldValue('own_shares', '');
                              }}>
                              <MenuItem value={OwnSharesType.Currency}>
                                {assetsCurrency}
                              </MenuItem>
                              <MenuItem value={OwnSharesType.Percentage}>
                                %
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Col>
                        <Col xs={8}>
                          <OwnSharesField
                            value={formik.values.own_shares}
                            type={formik.values.own_shares_type}
                            currency={toString(assetsCurrency)}
                            error={
                              formik.touched.own_shares &&
                              Boolean(formik.errors.own_shares)
                            }
                            helperText={
                              (formik.touched.own_shares &&
                                formik.errors.own_shares) ||
                              undefined
                            }
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row rowSpacing={3}>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('cash_term_deposits')}
                        name="cash"
                        type="text"
                        value={formik.values.cash}
                        onChange={formik.handleChange}
                        error={formik.touched.cash && !!formik.errors.cash}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={formik.touched.cash && formik.errors.cash}
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('listed_shares')}
                        name="listed_shares"
                        type="text"
                        value={formik.values.listed_shares}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.listed_shares &&
                          !!formik.errors.listed_shares
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={
                          formik.touched.listed_shares &&
                          formik.errors.listed_shares
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                  </Row>

                  <Row rowSpacing={3}>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('pension_funds')}
                        name="pension"
                        type="text"
                        value={formik.values.pension}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.pension && !!formik.errors.pension
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={
                          formik.touched.pension && formik.errors.pension
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <NumericFormat
                        fullWidth
                        customInput={TextField}
                        label={t('vehicles_and_other_assets')}
                        name="vehicles"
                        type="text"
                        value={formik.values.vehicles}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.vehicles && !!formik.errors.vehicles
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Typography variant="body2" color="text.disabled">
                                {assetsCurrency}
                              </Typography>
                            ),
                          },
                          htmlInput: {
                            min: 0,
                          },
                        }}
                        helperText={
                          formik.touched.vehicles && formik.errors.vehicles
                        }
                        thousandSeparator={' '}
                        decimalSeparator=","
                        allowedDecimalSeparators={[',', '.']}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Col>
                  </Row>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    endIcon={<ArrowRightIcon />}
                    disabled={!formik.isValid}
                    sx={{ width: { sm: 'fit-content' }, marginTop: 0.5 }}>
                    {t('continue')}
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};

const isOwnSharesPercentage = (value: string | number | null | undefined) => {
  return typeof value === 'string' && value.endsWith('%');
};

const getOwnSharesValue = (value: string | number | null | undefined) => {
  if (!value) return '';
  if (typeof value === 'string' && isOwnSharesPercentage(value)) {
    return value.replace('%', '');
  }
  return toString(value);
};
